(function () {
    if (window.__imageIdTelemetryInitialized) return;
    window.__imageIdTelemetryInitialized = true;

    const GA_MEASUREMENT_ID = 'G-3L1RQFKHV4';
    const GA_MAX_LOAD_DELAY_MS = 4000;
    const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
    const VISITOR_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
    const SESSION_MAX_AGE_SECONDS = SESSION_TIMEOUT_MS / 1000;
    const ID_PATTERN = /^[a-z0-9_-]{8,64}$/i;
    const ATTRIBUTION_PATTERN = /^[a-z0-9._~ +/@:-]{1,100}$/i;
    const COOKIE_NAMES = Object.freeze({
        visitor_id: 'iid_visitor_id',
        session_id: 'iid_session_id',
        visit_id: 'iid_visit_id',
        landing_path: 'iid_landing_path',
        landing_source: 'iid_landing_source',
        landing_medium: 'iid_landing_medium',
        landing_campaign: 'iid_landing_campaign',
    });

    function storageGet(storage, key) {
        try {
            return storage.getItem(key);
        } catch {
            return null;
        }
    }

    function storageSet(storage, key, value) {
        try {
            storage.setItem(key, value);
        } catch {
            // Storage can be unavailable in strict privacy modes.
        }
    }

    function createId(prefix) {
        const randomValue = globalThis.crypto?.randomUUID?.()
            || `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
        return `${prefix}_${randomValue.replaceAll('-', '')}`.slice(0, 64);
    }

    function persistentId(storage, key, prefix) {
        const stored = storageGet(storage, key);
        if (stored && ID_PATTERN.test(stored)) return stored;

        const id = createId(prefix);
        storageSet(storage, key, id);
        return id;
    }

    function visitorId() {
        const key = 'imageid_telemetry_visitor';
        const stored = storageGet(localStorage, key);
        const now = Date.now();
        let visitor = null;
        try {
            visitor = JSON.parse(stored || 'null');
        } catch {
            if (stored && ID_PATTERN.test(stored)) visitor = { id: stored, created_at: now };
        }

        if (
            !visitor
            || !ID_PATTERN.test(visitor.id || '')
            || !Number.isFinite(visitor.created_at)
            || now - visitor.created_at > VISITOR_MAX_AGE_SECONDS * 1000
        ) {
            visitor = { id: createId('v'), created_at: now };
        }
        storageSet(localStorage, key, JSON.stringify(visitor));
        return visitor.id;
    }

    function sessionId() {
        const now = Date.now();
        let session;
        try {
            session = JSON.parse(storageGet(localStorage, 'imageid_telemetry_session') || 'null');
        } catch {
            session = null;
        }

        if (
            !session
            || !ID_PATTERN.test(session.id || '')
            || !Number.isFinite(session.last_seen)
            || now - session.last_seen > SESSION_TIMEOUT_MS
        ) {
            session = { id: createId('s'), last_seen: now };
        } else {
            session.last_seen = now;
        }
        storageSet(localStorage, 'imageid_telemetry_session', JSON.stringify(session));
        return session.id;
    }

    function sanitizedAttribution(value) {
        const normalized = value?.trim().slice(0, 100) || null;
        return normalized && ATTRIBUTION_PATTERN.test(normalized) ? normalized : null;
    }

    function externalReferrerHost() {
        if (!document.referrer) return null;
        try {
            const referrer = new URL(document.referrer);
            if (referrer.origin === window.location.origin) return null;
            return sanitizedAttribution(referrer.hostname.toLowerCase());
        } catch {
            return null;
        }
    }

    function referrerMedium(host) {
        if (!host) return 'direct';
        return /(^|\.)(google|bing|yahoo|duckduckgo|baidu|yandex)\./i.test(host)
            ? 'organic'
            : 'referral';
    }

    function landingAttribution() {
        const storageKey = 'imageid_telemetry_landing';
        try {
            const stored = JSON.parse(storageGet(sessionStorage, storageKey) || 'null');
            if (stored?.landing_path) return stored;
        } catch {
            // Replace invalid stored attribution below.
        }

        const params = new URLSearchParams(window.location.search);
        const referrerHost = externalReferrerHost();
        const source = sanitizedAttribution(params.get('utm_source')) || referrerHost || 'direct';
        const attribution = {
            landing_path: window.location.pathname || '/',
            landing_source: source,
            landing_medium: sanitizedAttribution(params.get('utm_medium'))
                || referrerMedium(referrerHost),
            landing_campaign: sanitizedAttribution(params.get('utm_campaign')),
        };
        storageSet(sessionStorage, storageKey, JSON.stringify(attribution));
        return attribution;
    }

    function scannerIntent() {
        const params = new URLSearchParams(window.location.search);
        return sanitizedAttribution(
            params.get('intent') || params.get('scan') || params.get('scanner_intent')
        );
    }

    function analyticsPageUrl(attribution) {
        const url = new URL(window.location.pathname || '/', window.location.origin);
        if (url.pathname !== attribution.landing_path) return url.href;
        const campaignParams = {
            utm_source: attribution.landing_source === 'direct'
                || attribution.landing_medium === 'referral' ? null : attribution.landing_source,
            utm_medium: attribution.landing_medium === 'direct' ? null : attribution.landing_medium,
            utm_campaign: attribution.landing_campaign,
        };
        for (const [key, value] of Object.entries(campaignParams)) {
            if (value) url.searchParams.set(key, value);
        }
        return url.href;
    }

    function sameOriginReferrerPath() {
        if (!document.referrer) return null;
        try {
            const referrer = new URL(document.referrer);
            return referrer.origin === window.location.origin ? referrer.pathname || '/' : null;
        } catch {
            return null;
        }
    }

    function setCookie(name, value, maxAge) {
        if (value === null || value === undefined) return;
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        const lifetime = maxAge ? `; Max-Age=${maxAge}` : '';
        document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${lifetime}${secure}`;
    }

    const attribution = landingAttribution();
    const context = {
        visitor_id: visitorId(),
        session_id: sessionId(),
        visit_id: persistentId(sessionStorage, 'imageid_telemetry_visit', 't'),
        ...attribution,
        page_path: window.location.pathname || '/',
        page_url: analyticsPageUrl(attribution),
        referrer_path: sameOriginReferrerPath(),
        scanner_intent: scannerIntent(),
    };

    setCookie(COOKIE_NAMES.visitor_id, context.visitor_id, VISITOR_MAX_AGE_SECONDS);
    setCookie(COOKIE_NAMES.session_id, context.session_id, SESSION_MAX_AGE_SECONDS);
    setCookie(COOKIE_NAMES.visit_id, context.visit_id);
    setCookie(COOKIE_NAMES.landing_path, context.landing_path);
    setCookie(COOKIE_NAMES.landing_source, context.landing_source);
    setCookie(COOKIE_NAMES.landing_medium, context.landing_medium);
    setCookie(COOKIE_NAMES.landing_campaign, context.landing_campaign);
    window.imageIdTelemetryContext = Object.freeze({ ...context });

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
        window.dataLayer.push(arguments);
    };
    if (!window.__imageIdGaConfigured) {
        window.gtag('js', new Date());
        window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
        window.__imageIdGaConfigured = true;
    }
    window.gtag('event', 'page_view', {
        page_location: context.page_url,
        page_path: context.page_path,
        page_title: document.title,
        scanner_intent: context.scanner_intent,
        landing_source: context.landing_source,
        landing_medium: context.landing_medium,
        landing_campaign: context.landing_campaign,
    });

    let analyticsScriptRequested = false;
    let analyticsIdleHandle = null;
    let analyticsTimeoutHandle = null;
    function loadGoogleAnalytics() {
        if (analyticsScriptRequested) return;
        analyticsScriptRequested = true;
        if (analyticsIdleHandle !== null) window.cancelIdleCallback?.(analyticsIdleHandle);
        if (analyticsTimeoutHandle !== null) window.clearTimeout(analyticsTimeoutHandle);
        if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) return;

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.append(script);
    }

    function scheduleGoogleAnalyticsAfterLoad() {
        if (analyticsScriptRequested) return;
        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                if (typeof window.requestIdleCallback === 'function') {
                    analyticsIdleHandle = window.requestIdleCallback(loadGoogleAnalytics, {
                        timeout: GA_MAX_LOAD_DELAY_MS,
                    });
                } else {
                    window.setTimeout(loadGoogleAnalytics, 0);
                }
            });
        } else if (typeof window.requestIdleCallback === 'function') {
            analyticsIdleHandle = window.requestIdleCallback(loadGoogleAnalytics, {
                timeout: GA_MAX_LOAD_DELAY_MS,
            });
        }
    }

    function scheduleGoogleAnalytics() {
        analyticsTimeoutHandle = window.setTimeout(loadGoogleAnalytics, GA_MAX_LOAD_DELAY_MS);
        if (document.readyState === 'complete') {
            scheduleGoogleAnalyticsAfterLoad();
        } else {
            window.addEventListener?.('load', scheduleGoogleAnalyticsAfterLoad, { once: true });
        }
    }

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') loadGoogleAnalytics();
    });
    window.addEventListener?.('pagehide', loadGoogleAnalytics, { once: true });

    const body = JSON.stringify(context);
    const blob = new Blob([body], { type: 'application/json' });

    const sentWithBeacon = navigator.sendBeacon?.('/api/telemetry/page-view', blob);
    if (!sentWithBeacon) {
        fetch('/api/telemetry/page-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch(() => {});
    }

    scheduleGoogleAnalytics();
})();
